import { getElementsForViewport } from "./escapeRoomEditorViewportUtils";

function normScId(v) {
	if (v == null || v === "") return "";
	return String(v).trim();
}

const PLAY_GRAPH_VIEWPORTS = ["desktop", "tablet", "mobile"];

function playFindPageIndex(pages, tid) {
	return pages.findIndex((p) => String(p.pageId || p._id) === String(tid));
}

/** Índice de la primera página del escenario (menor `order`). */
function entryPageIndexForScenario(pages, scenarioId) {
	const sid = normScId(scenarioId);
	if (!sid) return -1;
	let best = -1;
	let bestOrder = Infinity;
	for (let i = 0; i < pages.length; i++) {
		if (normScId(pages[i]?.scenarioId) !== sid) continue;
		const o = Number(pages[i]?.order) || i;
		if (o < bestOrder) {
			bestOrder = o;
			best = i;
		}
	}
	return best;
}

/** Índices de página destino (nextPage / goToPage / modales / pistas). */
function walkOutgoingPageIndices(elements, pages, pageIdx, findIdx, out) {
	if (!elements?.length) return;
	for (const e of elements) {
		if (e.type === "button") {
			const a = e.content?.action;
			if (a === "nextPage") {
				const ni = Math.min(pages.length - 1, pageIdx + 1);
				out.add(ni);
			} else if (a === "goToPage" && e.content?.targetPageId) {
				const ix = findIdx(e.content.targetPageId);
				if (ix >= 0) out.add(ix);
			} else if (a === "openModal" && e.content?.modalDesign?.elements?.length) {
				walkOutgoingPageIndices(e.content.modalDesign.elements, pages, pageIdx, findIdx, out);
			}
		} else if (e.type === "hint") {
			const c = e.content || {};
			for (const key of ["hintModalDesign", "confirmModalDesign"]) {
				const des = c[key];
				if (des?.elements?.length) walkOutgoingPageIndices(des.elements, pages, pageIdx, findIdx, out);
			}
		}
	}
}

function collectOutgoingPageIndicesFromPlayPage(pages, pageIdx) {
	const findIdx = (tid) => playFindPageIndex(pages, tid);
	const out = new Set();
	for (const vp of PLAY_GRAPH_VIEWPORTS) {
		const els = getElementsForViewport(pages[pageIdx], vp) || [];
		walkOutgoingPageIndices(els, pages, pageIdx, findIdx, out);
	}
	return out;
}

/** Destinos escenario en un clic (supresión / compat). */
function collectForwardBranchScenarioIdsFromPlayPage(pages, pageIdx, viewportId) {
	const vps = viewportId && viewportId !== "desktop" ? [viewportId, "desktop"] : PLAY_GRAPH_VIEWPORTS;
	const findIdx = (tid) => playFindPageIndex(pages, tid);
	const page = pages[pageIdx];
	if (!page) return new Set();
	const currentSc = normScId(page.scenarioId);
	const out = new Set();
	for (const vp of vps) {
		const els = getElementsForViewport(page, vp) || [];
		walkOutgoingPageIndices(els, pages, pageIdx, findIdx, out);
	}
	const scenOut = new Set();
	for (const t of out) {
		const sc = normScId(pages[t]?.scenarioId);
		if (sc && sc !== currentSc) scenOut.add(sc);
	}
	return scenOut;
}

function playUfFind(parent, x) {
	const k = normScId(x);
	if (!k) return "";
	if (!parent.has(k)) parent.set(k, k);
	if (parent.get(k) === k) return k;
	const r = playUfFind(parent, parent.get(k));
	parent.set(k, r);
	return r;
}
function playUfUnion(parent, a, b) {
	const ra = playUfFind(parent, normScId(a));
	const rb = playUfFind(parent, normScId(b));
	if (ra && rb && ra !== rb) parent.set(ra, rb);
}

/**
 * Grupos exclusivos solo desde el grafo de botones:
 * 1) Misma página: varios destinos con distinto escenario.
 * 2) Mismo escenario padre S: varias ramas si desde alguna página de S hay clic a la *primera* página de otro escenario (≥2 hijos distintos).
 */
function computeExclusiveBranchGroupsFromPlay(pages) {
	const parent = new Map();
	const getOut = (pi) => collectOutgoingPageIndicesFromPlayPage(pages, pi);

	for (let i = 0; i < pages.length; i++) {
		const cur = normScId(pages[i]?.scenarioId);
		const scenFromPage = new Set();
		for (const t of getOut(i)) {
			const sc = normScId(pages[t]?.scenarioId);
			if (sc && sc !== cur) scenFromPage.add(sc);
		}
		if (scenFromPage.size >= 2) {
			const ids = [...scenFromPage];
			for (let j = 1; j < ids.length; j++) playUfUnion(parent, ids[0], ids[j]);
		}
	}

	const scenariosSeen = new Set();
	for (let p = 0; p < pages.length; p++) {
		const s = normScId(pages[p]?.scenarioId);
		if (!s || scenariosSeen.has(s)) continue;
		scenariosSeen.add(s);
		const childScenarios = new Set();
		for (let i = 0; i < pages.length; i++) {
			if (normScId(pages[i]?.scenarioId) !== s) continue;
			for (const t of getOut(i)) {
				const ts = normScId(pages[t]?.scenarioId);
				if (!ts || ts === s) continue;
				const ent = entryPageIndexForScenario(pages, ts);
				if (ent >= 0 && t === ent) childScenarios.add(ts);
			}
		}
		if (childScenarios.size >= 2) {
			const ids = [...childScenarios];
			for (let j = 1; j < ids.length; j++) playUfUnion(parent, ids[0], ids[j]);
		}
	}

	const allScenarioIds = new Set();
	for (const pg of pages) {
		const x = normScId(pg?.scenarioId);
		if (x) allScenarioIds.add(x);
	}
	const byRoot = new Map();
	allScenarioIds.forEach((id) => {
		const r = playUfFind(parent, id);
		if (!r) return;
		if (!byRoot.has(r)) byRoot.set(r, new Set());
		byRoot.get(r).add(id);
	});
	return Array.from(byRoot.values()).filter((g) => g.size >= 2);
}

function playSuppressBranchAlternates(landedScenarioId, pages, setSuppressed) {
	const landed = normScId(landedScenarioId);
	if (!landed) return;
	const groups = computeExclusiveBranchGroupsFromPlay(pages);
	const g = groups.find((gr) => gr.has(landed));
	if (!g || g.size < 2) return;
	setSuppressed((prev) => {
		const n = new Set();
		prev.forEach((x) => n.add(normScId(x)));
		g.forEach((id) => {
			const x = normScId(id);
			if (x && x !== landed) n.add(x);
		});
		return n;
	});
}

function scenariosVisitedUpToPlayPage(pages, pageIdx) {
	const seen = new Set();
	for (let i = 0; i <= pageIdx; i++) {
		const s = normScId(pages[i]?.scenarioId);
		if (s) seen.add(s);
	}
	return seen;
}

export {
	normScId,
	PLAY_GRAPH_VIEWPORTS,
	playFindPageIndex,
	entryPageIndexForScenario,
	walkOutgoingPageIndices,
	collectOutgoingPageIndicesFromPlayPage,
	collectForwardBranchScenarioIdsFromPlayPage,
	playUfFind,
	playUfUnion,
	computeExclusiveBranchGroupsFromPlay,
	playSuppressBranchAlternates,
	scenariosVisitedUpToPlayPage,
};
